import { showUserAlert } from "../../utils/userErrorMessage";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/client";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import TimeSelect from "../../components/ui/time-select";
import {
  clearDriverAssignment,
  DRIVER_HOLIDAY_BLOCK_MESSAGE,
  findDriverHolidayConflict,
} from "../../utils/driverHolidayUtils";

export default function GeneralEditForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);

  useEffect(() => {
    (async () => {
      const company = localStorage.getItem("email") || "";
      const { data } = await supabase
        .from("reservations")
        .select("*")
        .eq("id", id)
        .eq("company", company)
        .single();
      setForm(data);
    })();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateDriverHoliday = async () => {
    if (!String(form?.human_name || "").trim()) return;

    try {
      const conflict = await findDriverHolidayConflict([form], form.company || localStorage.getItem("email"));
      if (conflict) {
        showUserAlert(DRIVER_HOLIDAY_BLOCK_MESSAGE);
        setForm((prev) => clearDriverAssignment(prev));
      }
    } catch (error) {
      console.error("기사 휴일 확인 실패:", error);
      showUserAlert("기사 휴일 정보를 확인하지 못했습니다.");
      setForm((prev) => clearDriverAssignment(prev));
    }
  };

  const toNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
  };

  const handleUpdate = async () => {
    const payload = {
      ...form,
      time: form.time || null,
      arrival_time: form.arrival_time || null,
      round_trip_time: form.one_round === "왕복" ? form.round_trip_time || null : null,
      money: toNumberOrNull(form.money),
      driver_money: toNumberOrNull(form.driver_money),
      number_of_car: toNumberOrNull(form.number_of_car),
      days: toNumberOrNull(form.days),
    };

    const company = localStorage.getItem("email") || "";
    const { error } = await supabase
      .from("reservations")
      .update(payload)
      .eq("id", id)
      .eq("company", company);
    if (error) {
      console.error("예약 수정 실패:", error);
      showUserAlert("수정 실패: " + error.message);
    }
    else {
      showUserAlert("수정 완료");
      navigate(-1);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      const company = localStorage.getItem("email") || "";
      const { error } = await supabase
        .from("reservations")
        .delete()
        .eq("id", id)
        .eq("company", company);
      if (!error) {
        showUserAlert("삭제 완료");
        navigate(-1);
      }
    }
  };

  if (!form) return <div>로딩 중...</div>;

  return (
    <div className="px-4 pt-1 pb-4 space-y-2">
      <h2 className="text-lg font-semibold">일반 예약 수정</h2>
      <Card className="p-2">
        <CardContent className="bg-white">
          <div className="reservation-form-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="reservation-section-title">운행 정보</div>
            {/* 운행일 */}
            <div>
              <label className="block font-semibold mb-1">운행일</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => handleChange("date", e.target.value)}
                onBlur={validateDriverHoliday}
              />
            </div>

            {/* 차량 크기 */}
            <div>
              <label className="block font-semibold mb-1">차량 크기</label>
              <select
                className="w-full border rounded p-2"
                value={form.seat}
                onChange={(e) => handleChange("seat", e.target.value)}
              >
                {["대형", "중형", "우등", "카운티"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* 차량 대수 / 박 수 */}
            <div>
              <label className="block font-semibold mb-1">차량 대수</label>
              <Input
                type="number"
                value={form.number_of_car}
                onChange={(e) => handleChange("number_of_car", e.target.value)}
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">박 수</label>
              <Input
                type="number"
                value={form.days}
                onChange={(e) => handleChange("days", e.target.value)}
              />
            </div>

            {/* 편도/왕복 + 출발시간 */}
            <div>
              <label className="block font-semibold mb-1">편도/왕복</label>
              <select
                className="w-full border rounded p-2"
                value={form.one_round}
                onChange={(e) => handleChange("one_round", e.target.value)}
              >
                <option value="왕복">왕복</option>
                <option value="편도">편도</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-1">출발시간</label>
              <TimeSelect value={form.time || ""} onChange={(value) => handleChange("time", value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">도착 예상 시간</label>
              <TimeSelect value={form.arrival_time || ""} onChange={(value) => handleChange("arrival_time", value)} />
            </div>
            {form.one_round === "왕복" && (
              <div>
                <label className="block font-semibold mb-1">회차 시간</label>
                <TimeSelect value={form.round_trip_time || ""} onChange={(value) => handleChange("round_trip_time", value)} />
              </div>
            )}

            <div className="reservation-section-title">경로 정보</div>
            {/* 출발지 / 경유지 / 행선지 */}
            <div>
              <label className="block font-semibold mb-1">출발지</label>
              <Input value={form.start} onChange={(e) => handleChange("start", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">경유지</label>
              <Input value={form.transit} onChange={(e) => handleChange("transit", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">행선지</label>
              <Input value={form.destination} onChange={(e) => handleChange("destination", e.target.value)} />
            </div>

            {/* 사용자 / 연락처 */}
            <div>
              <label className="block font-semibold mb-1">사용자/노선명</label>
              <Input value={form.user} onChange={(e) => handleChange("user", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">사용자 전화번호</label>
              <Input value={form.user_phone || ""} onChange={(e) => handleChange("user_phone", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">네이버 지도 링크</label>
              <Input type="url" placeholder="https://map.naver.com/..." value={form.naver_map_url || ""} onChange={(e) => handleChange("naver_map_url", e.target.value)} />
            </div>

            <div className="reservation-section-title">금액 정보</div>
            <div>
              <label className="block font-semibold mb-1">받을 금액</label>
              <Input
                type="number"
                value={form.money}
                onChange={(e) => handleChange("money", e.target.value)}
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">기사 금액</label>
              <Input
                type="number"
                value={form.driver_money || ""}
                onChange={(e) => handleChange("driver_money", e.target.value)}
              />
            </div>

            <div className="reservation-section-title">기타</div>
            {/* 메모 */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block font-semibold mb-1">기타 메모</label>
              <Input value={form.memo || ""} onChange={(e) => handleChange("memo", e.target.value)} />
            </div>

            <div className="reservation-section-title">차량 및 기사</div>
            {/* 차량번호, 기사명, 전화번호 */}
            <div>
              <label className="block font-semibold mb-1">차량번호</label>
              <Input value={form.car_number || ""} onChange={(e) => handleChange("car_number", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">기사명</label>
              <Input value={form.human_name || ""} onChange={(e) => handleChange("human_name", e.target.value)} onBlur={validateDriverHoliday} />
            </div>
            <div>
              <label className="block font-semibold mb-1">기사 연락처</label>
              <Input value={form.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} />
            </div>

            {/* 버튼 */}
            <div className="reservation-action-row">
              <Button onClick={handleUpdate}>수정</Button>
              <Button variant="destructive" onClick={handleDelete}>삭제</Button>
              <Button onClick={() => navigate(-1)}>돌아가기</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
